import { NavLink } from "react-router-dom";

import { Button, Tooltip } from "@mui/material";

import {
  CHANGE_PASSWORD,
  NOTIFICATION_PREFERENCES,
  PROFILE_MANAGEMENT,
  SETTINGS,
  SYSTEM_PREFERENCE,
  TEAM_MANAGEMENT,
  TRANSACTION_PIN,
  TWO_FACTOR_AUTH,
} from "@zenith/routes/paths";
import { useCurrentRole } from "@zenith/features/AuthZ/useCurrentRole";
import { usePermissions } from "@zenith/features/AuthZ/usePermissions";
import { SidebarProps } from "@zenith/types/SidebarProps";
import useAppNavigation from "@zenith/hooks/appNavigation";
import { UserProfile } from "@zenith/assets/icons/UserProfile";
import { TransactionPin } from "@zenith/assets/icons/TransactionPin";
import { ChangePassword } from "@zenith/assets/icons/ChangePassword";
import { TwoFactorAuth } from "@zenith/assets/icons/TwoFactorAuth";
import { TeamManagement } from "@zenith/assets/icons/TeamManagement";
import Preference from "@zenith/assets/icons/Preference";
import { useCallback, useEffect, useState } from "react";
import { SetShowSideBar } from "@zenith/features/LandingPage";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";

export function SettingsSidebar(props: SidebarProps) {
  // Owners and admins hold settings:preferences, so only they see Notification.
  const role = useCurrentRole();
  const navigation = useAppNavigation();
  // The claim says which profile was used at sign-in, not the one in use now.
  const isAdmin = role === "admin";
  // BOAS-7641: creating and managing team members belongs to Admin and Manager.
  const { can } = usePermissions();
  const [isSidebarShortened, setSidebarShortened] = useState<boolean>(false);
  const isResized = useCallback((): boolean => {
    return window.innerWidth < 1024 && window.innerWidth > 630;
  }, []);
  const [tooltipsOpen, setTooltipsOpen] = useState<Record<string, boolean>>({});

  const handleTooltipToggle = (id: string, isOpen: boolean) => {
    setTooltipsOpen((prev) => ({ ...prev, [id]: isOpen }));
  };

  const handleBack = () => {
    navigation.navigateTo(SETTINGS);
  };

  useEffect(() => {
    const handleResize = () => {
      setSidebarShortened(isResized());
      if (window.innerWidth >= 630) {
        onSetShowSideBar(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isResized]);

  const sidebarTooltipProps = {
    componentsProps: {
      tooltip: {
        sx: {
          backgroundColor: "#373737",
          borderRadius: "8px",
          fontSize: "14px",
        },
      },
      arrow: {
        sx: {
          color: "#373737",
        },
      },
    },
  };
  const { onSetShowSideBar } = SetShowSideBar();

  return (
    <aside id="sidebar" className="flex h-full flex-col justify-between">
      <ul className="flex w-2/3 select-none list-none flex-col place-items-center gap-3 sm:w-full">
        <div className="mb-20 pt-0">
          <Button
            id="back-cart-summary"
            onClick={handleBack}
            className="text-base font-normal normal-case text-neutral-white hover:bg-white hover:text-primary-300"
          >
            <ChevronLeft fontSize="small" className="mr-1.5" />{" "}
            {isSidebarShortened ? "" : "Back to Main Menu"}
          </Button>
        </div>
        <li className="h-12 w-full">
          <NavLink
            id="profile-management-link"
            to={PROFILE_MANAGEMENT}
            className={`flex h-full place-items-center rounded-lg
                ${
                  props.location.pathname === PROFILE_MANAGEMENT
                    ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                    : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                } ${isSidebarShortened ? "grid grid-cols-3 pl-2" : "pl-1"}`}
            onClick={(e) => {
              props.handleNavLinkClick(e);
              onSetShowSideBar(false);
            }}
            onMouseEnter={() => handleTooltipToggle("profile", true)}
            onMouseLeave={() => handleTooltipToggle("profile", false)}
          >
            <UserProfile
              className={`py-auto mr-2.5 h-full w-5 ${
                isSidebarShortened ? "ml-8" : "ml-3"
              }                 
              `}
              color={
                props.location.pathname === PROFILE_MANAGEMENT
                  ? "menu-icon-primary"
                  : "menu-icon-white"
              }
            />
            {isSidebarShortened && tooltipsOpen["profile"] && (
              <Tooltip
                {...sidebarTooltipProps}
                title="Profile Management"
                placement="right"
                arrow
                disableInteractive={true}
                open={tooltipsOpen["profile"] || false}
                className="px-3 py-0"
              >
                <div></div>
              </Tooltip>
            )}
            <span
              className={`flex h-full items-center ${
                isSidebarShortened ? "hidden" : ""
              }`}
            >
              Profile Management
            </span>{" "}
          </NavLink>
        </li>
        {isAdmin && (
          <li className="h-12 w-full">
            <NavLink
              id="transaction-pin-link"
              to={TRANSACTION_PIN}
              className={`flex h-full place-items-center rounded-lg
                ${
                  props.location.pathname === TRANSACTION_PIN
                    ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                    : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                } ${isSidebarShortened ? "grid grid-cols-3 pl-2" : "pl-1"}`}
              onClick={(e) => {
                props.handleNavLinkClick(e);
                onSetShowSideBar(false);
              }}
              onMouseEnter={() => handleTooltipToggle("transaction-pin", true)}
              onMouseLeave={() => handleTooltipToggle("transaction-pin", false)}
            >
              <TransactionPin
                className={`py-auto mr-2.5 h-full w-5 ${
                  isSidebarShortened ? "ml-8" : "ml-3"
                } ${
                  props.location.pathname === TRANSACTION_PIN
                    ? "menu-icon-primary"
                    : "menu-icon-white"
                }`}
              />
              {isSidebarShortened && tooltipsOpen["transaction-pin"] && (
                <Tooltip
                  {...sidebarTooltipProps}
                  title="Transaction PIN"
                  placement="right"
                  arrow
                  disableInteractive={true}
                  open={tooltipsOpen["transaction-pin"] || false}
                  className="px-3 py-0"
                >
                  <div></div>
                </Tooltip>
              )}
              <span
                className={`flex h-full items-center ${
                  isSidebarShortened ? "hidden" : ""
                }`}
              >
                Transaction PIN
              </span>
            </NavLink>
          </li>
        )}
        <li className="h-12 w-full">
          <NavLink
            id="transaction-pin-link"
            to={CHANGE_PASSWORD}
            className={`flex h-full place-items-center rounded-lg
                ${
                  props.location.pathname === CHANGE_PASSWORD
                    ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                    : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                } ${isSidebarShortened ? "grid grid-cols-3 pl-2" : "pl-1"}`}
            onClick={(e) => {
              props.handleNavLinkClick(e);
              onSetShowSideBar(false);
            }}
            onMouseEnter={() => handleTooltipToggle("change-password", true)}
            onMouseLeave={() => handleTooltipToggle("change-password", false)}
          >
            <ChangePassword
              className={`py-auto mr-2.5 h-full w-5 ${
                isSidebarShortened ? "ml-8" : "ml-3"
              } `}
              color={
                props.location.pathname === CHANGE_PASSWORD
                  ? "menu-icon-primary"
                  : "menu-icon-white"
              }
            />
            {isSidebarShortened && tooltipsOpen["change-password"] && (
              <Tooltip
                {...sidebarTooltipProps}
                title="Change Passcode"
                placement="right"
                arrow
                disableInteractive={true}
                open={tooltipsOpen["change-password"] || false}
                className="px-3 py-0"
              >
                <div></div>
              </Tooltip>
            )}
            <span
              className={`flex h-full items-center ${
                isSidebarShortened ? "hidden" : ""
              }`}
            >
              Change Passcode
            </span>{" "}
          </NavLink>
        </li>
        {isAdmin && (
          <>
            <li className="h-12 w-full">
              <NavLink
                id="transaction-pin-link"
                to={TWO_FACTOR_AUTH}
                className={`flex h-full place-items-center rounded-lg
                ${
                  props.location.pathname === TWO_FACTOR_AUTH
                    ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                    : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                } ${isSidebarShortened ? "grid grid-cols-3 pl-2" : "pl-1"}`}
                onClick={(e) => {
                  props.handleNavLinkClick(e);
                  onSetShowSideBar(false);
                }}
                onMouseEnter={() => handleTooltipToggle("2fa", true)}
                onMouseLeave={() => handleTooltipToggle("2fa", false)}
              >
                <TwoFactorAuth
                  className={`py-auto mr-2.5 h-full w-5 ${
                    isSidebarShortened ? "ml-8" : "ml-3"
                  } `}
                  color={
                    props.location.pathname === TWO_FACTOR_AUTH
                      ? "menu-icon-primary"
                      : "menu-icon-white"
                  }
                />
                {isSidebarShortened && tooltipsOpen["2fa"] && (
                  <Tooltip
                    {...sidebarTooltipProps}
                    title="2F Authentication"
                    placement="right"
                    arrow
                    disableInteractive={true}
                    open={tooltipsOpen["2fa"] || false}
                    className="px-3 py-0"
                  >
                    <div></div>
                  </Tooltip>
                )}
                <span
                  className={`flex h-full items-center ${
                    isSidebarShortened ? "hidden" : ""
                  }`}
                >
                  2F Authentication
                </span>
              </NavLink>
            </li>
          </>
        )}
        {/* BOAS-7641: Admin and Manager create and manage team members. */}
        {can("team:manage") && (
          <li className="h-12 w-full">
            <NavLink
              id="team-management-link"
              to={TEAM_MANAGEMENT}
              className={`flex h-full place-items-center rounded-lg
                ${
                  props.location.pathname === TEAM_MANAGEMENT
                    ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                    : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                } ${isSidebarShortened ? "grid grid-cols-3 pl-2" : "pl-1"}`}
              onClick={(e) => {
                props.handleNavLinkClick(e);
                onSetShowSideBar(false);
              }}
              onMouseEnter={() => handleTooltipToggle("team-management", true)}
              onMouseLeave={() => handleTooltipToggle("team-management", false)}
            >
              <TeamManagement
                className={`py-auto mr-2.5 h-full w-5 ${
                  isSidebarShortened ? "ml-8" : "ml-3"
                } 
                  `}
                stroke={
                  props.location.pathname === TEAM_MANAGEMENT
                    ? "menu-icon-primary"
                    : "menu-icon-white"
                }
              />
              {isSidebarShortened && tooltipsOpen["team-management"] && (
                <Tooltip
                  {...sidebarTooltipProps}
                  title="Team Management"
                  placement="right"
                  arrow
                  disableInteractive={true}
                  open={tooltipsOpen["team-management"] || false}
                  className="px-3 py-0"
                >
                  <div></div>
                </Tooltip>
              )}
              <span
                className={`flex h-full items-center ${
                  isSidebarShortened ? "hidden" : ""
                }`}
              >
                Team Management
              </span>
            </NavLink>
          </li>
        )}
        {isAdmin && (
          <>
            {role === "admin" && (
              <li className="h-12 w-full">
                <NavLink
                  id="notification-preferences-link"
                  to={NOTIFICATION_PREFERENCES}
                  className={`flex h-full place-items-center rounded-lg
                ${
                  props.location.pathname === NOTIFICATION_PREFERENCES
                    ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                    : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                } ${isSidebarShortened ? "grid grid-cols-3 pl-2" : "pl-1"}`}
                  onClick={(e) => {
                    props.handleNavLinkClick(e);
                    onSetShowSideBar(false);
                  }}
                >
                  <Preference
                    className={`py-auto mr-2.5 h-full w-5 ${
                      isSidebarShortened ? "ml-8" : "ml-3"
                    } ${
                      props.location.pathname === NOTIFICATION_PREFERENCES
                        ? "menu-icon-primary"
                        : "menu-icon-white"
                    }`}
                  />
                  <span
                    className={`flex h-full items-center ${
                      isSidebarShortened ? "hidden" : ""
                    }`}
                  >
                    Notification
                  </span>
                </NavLink>
              </li>
            )}
            <li className="h-12 w-full">
              <NavLink
                id="system-preference-link"
                to={SYSTEM_PREFERENCE}
                className={`flex h-full place-items-center rounded-lg
                ${
                  props.location.pathname === SYSTEM_PREFERENCE
                    ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                    : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                } ${isSidebarShortened ? "grid grid-cols-3 pl-2" : "pl-1"}`}
                onClick={(e) => {
                  props.handleNavLinkClick(e);
                  onSetShowSideBar(false);
                }}
                onMouseEnter={() =>
                  handleTooltipToggle("system-preference", true)
                }
                onMouseLeave={() =>
                  handleTooltipToggle("system-preference", false)
                }
              >
                <Preference
                  className={`py-auto mr-2.5 h-full w-5 ${
                    isSidebarShortened ? "ml-8" : "ml-3"
                  } ${
                    props.location.pathname === SYSTEM_PREFERENCE
                      ? "menu-icon-primary"
                      : "menu-icon-white"
                  }`}
                />
                {isSidebarShortened && tooltipsOpen["system-preference"] && (
                  <Tooltip
                    {...sidebarTooltipProps}
                    title="System Preference"
                    placement="right"
                    arrow
                    disableInteractive={true}
                    open={tooltipsOpen["system-preference"] || false}
                    className="px-3 py-0"
                  >
                    <div></div>
                  </Tooltip>
                )}
                <span
                  className={`flex h-full items-center ${
                    isSidebarShortened ? "hidden" : ""
                  }`}
                >
                  System Preference
                </span>
              </NavLink>
            </li>
          </>
        )}
      </ul>
    </aside>
  );
}

export default SettingsSidebar;
